//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import * as RadixDialog from '@radix-ui/react-dialog';
import React, { RefObject, forwardRef } from 'react';
import styled, { css } from 'styled-components';

import { getRgbaForThemeHex } from '@utils/getRgbaForThemeHex';

/**
 * `Dialog` is a flexible and accessible dialog component capable of containing a variety of trigger and content components.
 *
 * Note: When using custom components as triggers or within the menu, it's crucial to apply the `forwardRef` pattern
 * to maintain functionality. Failure to do so may result in unresponsive or incorrectly functioning dropdowns, leading
 * to potential confusion about the component's behavior.
 * 
 * For example, if you want to use our buttons from the atoms folder we can consider using them like:
 * <Button buttonStyles={DefaultButtonStyles}>Label</Button>
 *
 * @example
 * This example showcases the `Dialog` with custom trigger, content and state handler.
 *
 * const [isOpen, setIsOpen] = useState(false);
 * return (
 *   <Dialog open={isOpen} onOpenChange={setIsOpen}>
 *     <Dialog.Trigger asChild>
 *       <Button
 *         buttonStyles={DefaultButtonStyles}
 *         onClick={() => setIsOpen(true)}
 *       >
 *         Open dialog
 *       </Button>
 *     </Dialog.Trigger>
 *     <Dialog.Content onClose={() => setIsOpen(false)}>
 *       <Dialog.Title asChild>
 *         <HeadingText>Dialog Title</HeadingText>
 *       </Dialog.Title>
 *       <Dialog.Description asChild>
 *         <BodyText>
 *           This is basic dialog, close me with esc key or with click outside.
 *         </BodyText>
 *       </Dialog.Description>
 *       <Dialog.Close asChild>
 *         <DefaultIconButton iconName='close' onClick={() => setIsOpen(false)} />
 *       </Dialog.Close>
 *     </Dialog.Content>
 *   </Dialog>
 * );

 */
export const Dialog = (props: RadixDialog.DialogProps) => (
  <RadixDialog.Root {...props} />
);

const DialogOverlay = styled(RadixDialog.Overlay)<{
  $shouldHideOverlay: boolean;
  $zIndexOverlayOverride?: string;
}>(
  ({ $shouldHideOverlay, $zIndexOverlayOverride, theme }) => css`
    position: fixed;
    inset: ${theme.spacings[0]};
    z-index: ${$zIndexOverlayOverride ?? theme.zindex.modalBackdrop};
    background-color: ${$shouldHideOverlay
      ? 'transparent'
      : getRgbaForThemeHex(theme.colors.gray8, 0.8)};
  `,
);

export interface DialogContentProps extends RadixDialog.DialogContentProps {
  onClose?: () => void;
  /**
   * The Radix-based portal is not visible when used in conjunction with custom modals.
   * To address this issue, we will attach the portal to a static element using `portalContainer`.
   */
  portalContainer?: HTMLDivElement | null;
  ref?: RefObject<HTMLDivElement>;
  shouldHideOverlay?: boolean;
  zIndexOverlayOverride?: string;
}

const DialogContent = forwardRef<HTMLDivElement, DialogContentProps>(
  (
    {
      children,
      className,
      onClose,
      portalContainer,
      shouldHideOverlay = false,
      zIndexOverlayOverride,
      ...contentProps
    },
    ref,
  ) => {
    return (
      <RadixDialog.Portal container={portalContainer}>
        <DialogOverlay
          $shouldHideOverlay={shouldHideOverlay}
          $zIndexOverlayOverride={zIndexOverlayOverride}
        />
        <CenteredContent
          className={`unison-component ${className ?? ''}`}
          onEscapeKeyDown={onClose}
          onPointerDownOutside={onClose}
          ref={ref}
          {...contentProps}
        >
          {children}
        </CenteredContent>
      </RadixDialog.Portal>
    );
  },
);

DialogContent.displayName = 'DialogContent';

const CenteredContent = styled(RadixDialog.Content)(
  ({ theme }) => `
    position: fixed;
    top: 50%;
    left: 50%;
    z-index: ${theme.zindex.modal};
    transform: translate(-50%, -50%);
  `,
);

Dialog.Content = DialogContent;
Dialog.ContentWithoutPortal = RadixDialog.Content;
Dialog.Overlay = DialogOverlay;
Dialog.Portal = RadixDialog.Portal;
Dialog.Trigger = RadixDialog.Trigger;
Dialog.Title = RadixDialog.Title;
Dialog.Description = RadixDialog.Description;
Dialog.Close = RadixDialog.Close;
